import { changePasswordUserSystem, createUserSystem, deleteUserSystem, forgotPassword, getAuthProfile, getUserRoleWithoutAdmin, getUserSystem, requestAuthLoginPassword, requestKeepAlive, resendVerifyEmail, resetPassword, setInitialPassword, updateFactory, updateUserProfile, updateUserSystem, verifyRegistrationToken } from 'services/authAPI';
import {
  AuthProfileResponse,
  ChangePasswordUserSystemInput,
  ChangePasswordUserSystemResponse,
  CreateUserSystemInput,
  CreateUserSystemResponse,
  DeleteUserSystemInput,
  ForgotPasswordInput,
  ForgotPasswordResponse,
  InputUpdateFactory,
  InputUpdateUserProfile,
  KeepAliveInput,
  LoginPasswordInput,
  LoginPasswordResponse,
  RequestOPTAuthInput,
  ResetPasswordInput,
  ResetPasswordResponse,
  ResponseKeepAlive,
  ResponseRequestAuthOTP,
  ResponseSubmitAuthOTP,
  ResendVerifyEmailInput,
  ResendVerifyEmailResponse,
  SetInitialPasswordInput,
  SetInitialPasswordResponse,
  SubmitOTPAuthInput,
  UpdateFactoryResponse,
  UpdateUserProfileResponse,
  UpdateUserSystemInput,
  UserRoleWithoutAdminResponse,
  UserSystemInput,
  UserSystemResponse,
  VerifyRegistrationTokenInput,
  VerifyRegistrationTokenResponse,
} from 'services/interface'
import { requestAuthRequestOtp, requestAuthLogin } from 'services/authAPI'
import { UseMutationResult, useMutation, UseQueryResult, useQuery } from '@tanstack/react-query';

export const useRequestAuthOtp = (): UseMutationResult<
  ResponseRequestAuthOTP,
  Error,
  RequestOPTAuthInput,
  unknown
> => {
  return useMutation<ResponseRequestAuthOTP, Error, RequestOPTAuthInput, unknown>({
    mutationFn: (input: RequestOPTAuthInput): Promise<ResponseRequestAuthOTP> => requestAuthRequestOtp(input),
  })
}

export const useSubmitAuthOtp = (): UseMutationResult<
  ResponseSubmitAuthOTP,
  Error,
  SubmitOTPAuthInput,
  unknown
> => {
  return useMutation<ResponseSubmitAuthOTP, Error, SubmitOTPAuthInput, unknown>({
    mutationFn: (input: SubmitOTPAuthInput): Promise<ResponseSubmitAuthOTP> => requestAuthLogin(input),
  })
}

export const useGetAuthProfile = (): UseQueryResult<AuthProfileResponse, Error> => {
  return useQuery<AuthProfileResponse, Error>({
    queryKey: ['profile'],
    queryFn: () => getAuthProfile(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}

export const useRequestKeepAlive = (): UseMutationResult<
  ResponseKeepAlive,
  Error,
  KeepAliveInput,
  unknown
> => {
  return useMutation<ResponseKeepAlive, Error, KeepAliveInput, unknown>({
    mutationFn: (input: KeepAliveInput): Promise<ResponseKeepAlive> => requestKeepAlive(input),
  })
}

export const useUpdateFactory = (): UseMutationResult<
  UpdateFactoryResponse,
  Error,
  InputUpdateFactory,
  unknown
> => {
  return useMutation<UpdateFactoryResponse, Error, InputUpdateFactory, unknown>({
    mutationFn: (input: InputUpdateFactory): Promise<UpdateFactoryResponse> => updateFactory(input),
  })
}

export const useUserUpdateProfile = (): UseMutationResult<
  UpdateUserProfileResponse,
  Error,
  InputUpdateUserProfile,
  unknown
> => {
  return useMutation<UpdateUserProfileResponse, Error, InputUpdateUserProfile, unknown>({
    mutationFn: (input: InputUpdateUserProfile): Promise<UpdateUserProfileResponse> => updateUserProfile(input),
  })
}

export const useGetUserRoleWithoutAdmin = (): UseQueryResult<UserRoleWithoutAdminResponse, Error> => {
  return useQuery<UserRoleWithoutAdminResponse, Error>({
    queryKey: ['profile'],
    queryFn: () => getUserRoleWithoutAdmin(),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })
}



export const useGetUserSystem = (input: UserSystemInput): UseQueryResult<UserSystemResponse, Error> => {
  return useQuery<UserSystemResponse, Error>({
    queryKey: ['user_system', input],
    queryFn: () => getUserSystem(input),
    staleTime: 1000 * 60 * 60 * 24, // Uncomment if you need to set staleTime
  })
}

export const useCreateUserSystem = (): UseMutationResult<
  CreateUserSystemResponse,
  Error,
  CreateUserSystemInput,
  unknown
> => {
  return useMutation<CreateUserSystemResponse, Error, CreateUserSystemInput, unknown>({
    mutationFn: (input: CreateUserSystemInput): Promise<CreateUserSystemResponse> => createUserSystem(input),
  })
}

export const useDeleteUserSystem = (): UseMutationResult<
  CreateUserSystemResponse,
  Error,
  DeleteUserSystemInput,
  unknown
> => {
  return useMutation<CreateUserSystemResponse, Error, DeleteUserSystemInput, unknown>({
    mutationFn: (input: DeleteUserSystemInput): Promise<CreateUserSystemResponse> => deleteUserSystem(input),
  })
}

export const useUpdateUserSystem = (): UseMutationResult<
  CreateUserSystemResponse,
  Error,
  UpdateUserSystemInput,
  unknown
> => {
  return useMutation<CreateUserSystemResponse, Error, UpdateUserSystemInput, unknown>({
    mutationFn: (input: UpdateUserSystemInput): Promise<CreateUserSystemResponse> => updateUserSystem(input),
  })
}

export const useLoginPassword = (): UseMutationResult<
  LoginPasswordResponse,
  Error,
  LoginPasswordInput,
  unknown
> => {
  return useMutation<LoginPasswordResponse, Error, LoginPasswordInput, unknown>({
    mutationFn: (input: LoginPasswordInput): Promise<LoginPasswordResponse> => requestAuthLoginPassword(input),
  })
}

export const useChangePasswordUserSystem = (): UseMutationResult<
  ChangePasswordUserSystemResponse,
  Error,
  ChangePasswordUserSystemInput,
  unknown
> => {
  return useMutation<ChangePasswordUserSystemResponse, Error, ChangePasswordUserSystemInput, unknown>({
    mutationFn: (input: ChangePasswordUserSystemInput): Promise<ChangePasswordUserSystemResponse> =>
      changePasswordUserSystem(input),
  })
}

export const useForgotPassword = (): UseMutationResult<
  ForgotPasswordResponse,
  Error,
  ForgotPasswordInput,
  unknown
> => {
  return useMutation<ForgotPasswordResponse, Error, ForgotPasswordInput, unknown>({
    mutationFn: (input: ForgotPasswordInput): Promise<ForgotPasswordResponse> => forgotPassword(input),
  })
}

export const useResetPassword = (): UseMutationResult<
  ResetPasswordResponse,
  Error,
  ResetPasswordInput,
  unknown
> => {
  return useMutation<ResetPasswordResponse, Error, ResetPasswordInput, unknown>({
    mutationFn: (input: ResetPasswordInput): Promise<ResetPasswordResponse> => resetPassword(input),
  })
}

export const useVerifyRegistrationToken = (): UseMutationResult<
  VerifyRegistrationTokenResponse,
  Error,
  VerifyRegistrationTokenInput,
  unknown
> => {
  return useMutation<VerifyRegistrationTokenResponse, Error, VerifyRegistrationTokenInput, unknown>({
    mutationFn: (input: VerifyRegistrationTokenInput): Promise<VerifyRegistrationTokenResponse> => verifyRegistrationToken(input),
  })
}

export const useSetInitialPassword = (): UseMutationResult<
  SetInitialPasswordResponse,
  Error,
  SetInitialPasswordInput,
  unknown
> => {
  return useMutation<SetInitialPasswordResponse, Error, SetInitialPasswordInput, unknown>({
    mutationFn: (input: SetInitialPasswordInput): Promise<SetInitialPasswordResponse> => setInitialPassword(input),
  })
}

export const useResendVerifyEmail = (): UseMutationResult<
  ResendVerifyEmailResponse,
  Error,
  ResendVerifyEmailInput,
  unknown
> => {
  return useMutation<ResendVerifyEmailResponse, Error, ResendVerifyEmailInput, unknown>({
    mutationFn: (input: ResendVerifyEmailInput): Promise<ResendVerifyEmailResponse> => resendVerifyEmail(input),
  })
}

